import { useState, useEffect, useMemo, type CSSProperties } from "react";
import { useWorkspaceMembers } from "../../hooks/use-workspace-members";
import { useGroups, useMatterRestriction, useRestrictMatter } from "../../hooks/use-policy";
import { Avatar, showToast } from "../primitives";

/**
 * №11.2.4 (D-W2 inline) — restrict a matter from its record page.
 *
 * A restricted matter (and everything referencing it via the G9 join — time,
 * arrangements, invoices, trust, files, activities, register/party rows) is
 * hidden from members not on the allow-list; conflict checks return a sealed
 * hit; statutory reports are never row-filtered. The allow-list is over
 * workspace MEMBERS and GROUPS (a group grants access to all its members).
 * Owners always retain access. This is the last piece of №11.2.4 — the panel
 * (RolesAccessPanel) ships areas/roles/groups; restriction lives on the record.
 */
export interface RestrictMatterModalProps {
  open: boolean;
  onClose: () => void;
  matterId: string;
  /** Matter title for the dialog header. */
  matterName: string;
}

export function RestrictMatterModal({ open, onClose, matterId, matterName }: RestrictMatterModalProps) {
  const { data: memberData } = useWorkspaceMembers();
  const members = useMemo(() => memberData?.members ?? [], [memberData]);
  const { data: groups = [] } = useGroups();
  const current = useMatterRestriction(open ? matterId : undefined);
  const restrictMatter = useRestrictMatter();

  const [restricted, setRestricted] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Seed from the live restriction whenever the dialog opens / the read lands.
  const loaded = current.data?.restriction;
  useEffect(() => {
    if (!open) return;
    const r = loaded?.access === "restricted";
    setRestricted(!!r);
    setSelected(new Set(loaded?.allowed ?? []));
    setBusy(false);
  }, [open, loaded]);

  if (!open) return null;

  const owners = members.filter((m) => m.role === "owner");
  const nonOwners = members.filter((m) => m.role !== "owner");

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Persisted allow-list = selected non-owner members + selected groups.
  const memberIds = new Set(nonOwners.map((m) => m.id));
  const groupIds = new Set(groups.map((g) => g.id));
  const allowed = restricted
    ? [...selected].filter((id) => memberIds.has(id) || groupIds.has(id))
    : [];

  async function apply() {
    setBusy(true);
    try {
      await restrictMatter.mutateAsync({
        matter_id: matterId,
        access: restricted ? "restricted" : "workspace",
        allowed: restricted ? allowed : [],
      });
      showToast(
        restricted
          ? `Restricted '${matterName}' to ${allowed.length} member${allowed.length === 1 ? "" : "s"}/group${allowed.length === 1 ? "" : "s"} (plus owners)`
          : `'${matterName}' is workspace-visible again`,
      );
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't update matter access", "error", true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div style={modal} role="dialog" aria-label="Restrict matter">
        <div style={titleRow}>
          <i className="ti ti-lock" aria-hidden="true" />
          Restrict matter — {matterName}
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 14 }}>
          A restricted matter and everything on it — time, invoices, trust, files, register and parties — is hidden from
          anyone not on the list. Conflict checks that touch it return a sealed hit. Owners always have access.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          <label style={radioRow}>
            <input type="radio" checked={!restricted} onChange={() => setRestricted(false)} />
            <span>
              <strong style={{ fontWeight: 500 }}>Workspace-visible</strong>
              <span style={hint}>Everyone in the firm can see this matter.</span>
            </span>
          </label>
          <label style={radioRow}>
            <input type="radio" checked={restricted} onChange={() => setRestricted(true)} />
            <span>
              <strong style={{ fontWeight: 500 }}>Restricted</strong>
              <span style={hint}>Only the people and groups you pick below (plus owners).</span>
            </span>
          </label>
        </div>

        {restricted && (
          <div style={{ overflowY: "auto", flex: 1, border: "var(--kuvio-border-default)", borderRadius: "var(--border-radius-md)" }}>
            {groups.length > 0 && (
              <>
                <div style={sectionHead}>Groups</div>
                {groups.map((g) => (
                  <label key={g.id} style={pickRow}>
                    <input type="checkbox" checked={selected.has(g.id)} onChange={() => toggle(g.id)} />
                    <i className="ti ti-users" aria-hidden="true" style={{ color: "var(--color-text-tertiary)" }} />
                    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <span style={{ color: "var(--color-text-primary)" }}>{g.name}</span>
                      <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
                        {g.members.length} member{g.members.length === 1 ? "" : "s"}
                      </span>
                    </span>
                  </label>
                ))}
              </>
            )}
            <div style={sectionHead}>Members</div>
            {owners.map((m) => (
              <PickMember key={m.id} m={m} checked disabled tooltip="Owners always have access" />
            ))}
            {nonOwners.map((m) => (
              <PickMember key={m.id} m={m} checked={selected.has(m.id)} onToggle={() => toggle(m.id)} />
            ))}
            {members.length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: "var(--color-text-tertiary)" }}>No members yet.</div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={busy} style={{ fontSize: 13, padding: "8px 14px" }}>
            Cancel
          </button>
          <button type="button" onClick={() => void apply()} disabled={busy || current.isLoading} style={saveBtn(busy)}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, fontFamily: "var(--font-sans)",
};
const modal: CSSProperties = {
  background: "var(--color-background-primary)", border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)", padding: 24, width: 460, maxWidth: "92vw", maxHeight: "82vh",
  display: "flex", flexDirection: "column",
};
const titleRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 6, fontSize: 15, fontWeight: 500, marginBottom: 6, color: "var(--color-text-primary)",
};
const radioRow: CSSProperties = { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "var(--color-text-primary)", cursor: "pointer" };
const hint: CSSProperties = { display: "block", fontSize: 12, color: "var(--color-text-tertiary)" };
const sectionHead: CSSProperties = {
  padding: "6px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)", background: "var(--color-background-secondary, transparent)",
  position: "sticky", top: 0,
};
const pickRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", fontSize: 13, cursor: "pointer",
  borderBottom: "0.5px solid var(--color-border-secondary, transparent)",
};
const saveBtn = (busy: boolean): CSSProperties => ({
  fontSize: 13, padding: "8px 14px", borderRadius: "var(--border-radius-md)", border: "none", fontWeight: 500,
  fontFamily: "var(--font-sans)", cursor: busy ? "default" : "pointer",
  background: "var(--c-blue-600, var(--color-accent, #2563eb))", color: "#fff", opacity: busy ? 0.7 : 1,
});

function PickMember({
  m, checked, disabled, tooltip, onToggle,
}: {
  m: { id: string; name: string; email: string };
  checked: boolean; disabled?: boolean; tooltip?: string; onToggle?: () => void;
}) {
  return (
    <label title={tooltip} style={{ ...pickRow, opacity: disabled ? 0.7 : 1, cursor: disabled ? "default" : "pointer" }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onToggle} />
      <Avatar persona="gray" size="xs" name={m.name || m.email} />
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {m.name || m.email}
        </span>
        {m.name && <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{m.email}</span>}
      </span>
    </label>
  );
}
